import { Box, Card, CircularProgress, Tab, Tabs, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { GetRecentTrackingChangesForAnnualPlan } from '../../../services/TrackingChangeService'
import { TabDisplay } from '../../customComponents/TabDisplay'
import {
  type TrackingChangeModel,
  type TaskTrackingChangeGroupModel,
  type OrganizationTrackingChangeGroupModel,
  type RecentTrackingChangesResponseModel
} from '../../../models/TrackingChange'
import { RecentChangesList } from './RecentChangesList'
import { toast } from 'react-toastify'

interface Props {
  annualPlanId: number
  rcnId: number
  changeTab: (tab: number) => void
  onResolved?: () => void
}

const filterTaskChanges = (task: TaskTrackingChangeGroupModel, showAll: boolean): TaskTrackingChangeGroupModel => ({
  ...task,
  changes: task.changes.filter(change => showAll || !change.isResolved)
})

const filterOrganizationTaskGroups = (org: OrganizationTrackingChangeGroupModel, showAll: boolean): OrganizationTrackingChangeGroupModel => ({
  ...org,
  taskGroups: org.taskGroups
    .map(task => filterTaskChanges(task, showAll))
    .filter(task => task.changes.length > 0)
})

const replaceChangeInTaskGroup = (task: TaskTrackingChangeGroupModel, updatedChange: TrackingChangeModel): TaskTrackingChangeGroupModel => ({
  ...task,
  changes: task.changes.map(change => change.id === updatedChange.id ? updatedChange : change)
})

const applyChangeUpdateToOrganization = (org: OrganizationTrackingChangeGroupModel, updatedChange: TrackingChangeModel): OrganizationTrackingChangeGroupModel => ({
  ...org,
  taskGroups: org.taskGroups.map(task => replaceChangeInTaskGroup(task, updatedChange))
})

export const RecentChangesCard = (props: Props): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [recentChanges, setRecentChanges] = useState<RecentTrackingChangesResponseModel>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.annualPlanId == null) return

      const response = await GetRecentTrackingChangesForAnnualPlan(props.annualPlanId, true)
      if (response.success) {
        setRecentChanges(response.response)
      } else {
        for (const error of response.errors) {
          toast.error(error)
        }
      }
      setIsLoading(false)
    }

    void fetchData()
  }, [props.annualPlanId, props.rcnId])

  const getFilteredOrganizations = (showAll: boolean): OrganizationTrackingChangeGroupModel[] => {
    if (recentChanges == null) return []

    return recentChanges.organizations
      .map(org => filterOrganizationTaskGroups(org, showAll))
      .filter(org => org.taskGroups.length > 0)
  }

  const handleChangeUpdate = (updatedChange: TrackingChangeModel): void => {
    if (recentChanges == null) return

    setRecentChanges({
      ...recentChanges,
      organizations: recentChanges.organizations.map(org => applyChangeUpdateToOrganization(org, updatedChange))
    })
  }

  return (
    <Card data-testid='recentChangesCard' sx={{ backgroundColor: 'var(--grey-100)', minHeight: '3em', border: '1px solid var(--border)' }}>
      <Typography variant='h3' color='#021E40' textAlign='center' marginTop='8.5px'>Recent Changes</Typography>

      <Tabs value={selectedTab} onChange={(e, val) => { setSelectedTab(val) }}>
        <Tab data-testid='unresolvedChangesTab' label='Unresolved' value={0} sx={{ textTransform: 'none' }} />
        <Tab data-testid='allChangesTab' label='All' value={1} sx={{ textTransform: 'none' }} />
      </Tabs>

      <TabDisplay index={0} value={selectedTab}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {isLoading
            ? <Box sx={{ textAlign: 'center' }}><CircularProgress /></Box>
            : <>
              {getFilteredOrganizations(false).length > 0
                ? getFilteredOrganizations(false).map(org => (
                  <RecentChangesList
                    key={org.organizationId}
                    organizationChangeModel={org}
                    onChangeUpdate={handleChangeUpdate}
                    changeTab={props.changeTab}
                    onResolved={props.onResolved}
                  />
                ))
                : <Typography variant='body1' padding='12px'>No unresolved changes</Typography>}
            </>
          }
        </Box>
      </TabDisplay>

      <TabDisplay index={1} value={selectedTab}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {isLoading
            ? <Box sx={{ textAlign: 'center' }}><CircularProgress /></Box>
            : <>
              {getFilteredOrganizations(true).length > 0
                ? getFilteredOrganizations(true).map(org => (
                  <RecentChangesList
                    key={org.organizationId}
                    organizationChangeModel={org}
                    onChangeUpdate={handleChangeUpdate}
                    changeTab={props.changeTab}
                    onResolved={props.onResolved}
                  />
                ))
                : <Typography variant='body1' padding='12px'>No changes</Typography>}
            </>
          }
        </Box>
      </TabDisplay>
    </Card>
  )
}
